// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Newick format parser in JavaScript.
//
// Copyright (c) edited by Miguel Pignatelli 2014, based on Jason Davies 2010.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
//
// Example tree (from http://en.wikipedia.org/wiki/Newick_format):
//
//   (A:0.1,B:0.2,(C:0.3,D:0.4)E:0.5)F;
//
// Produces:
//   { name: "F", children: [
//       { name: "A", branch_length: 0.1 },
//       { name: "B", branch_length: 0.2 },
//       { name: "E", branch_length: 0.5, children: [
//           { name: "C", branch_length: 0.3 },
//           { name: "D", branch_length: 0.4 }
//       ]}
//   ]}

export function parseNewick(s) {
  const ancestors = [];
  let tree = {};
  const tokens = s.split(/\s*(;|\(|\)|,|:)\s*/);

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    switch (token) {
      case "(": {
        const subtree = {};
        tree.children = [subtree];
        ancestors.push(tree);
        tree = subtree;
        break;
      }
      case ",": {
        const subtree = {};
        ancestors[ancestors.length - 1].children.push(subtree);
        tree = subtree;
        break;
      }
      case ")":
        tree = ancestors.pop();
        break;
      case ":":
        break;
      default: {
        const prev = tokens[i - 1];
        if (prev === ")" || prev === "(" || prev === ",") {
          tree.name = token;
        } else if (prev === ":") {
          tree.branch_length = parseFloat(token);
        }
      }
    }
  }

  return tree;
}

// Serialise a built graph (or a raw root node) back to a Newick string.
//
// Inverse of parseNewick: `buildGraph(serialiseNewick(g))` reproduces the same
// topology, branch lengths, and internal-node names. Leaf identity travels on
// `node.id` (traverseTree moves a leaf's name into id and deletes name), so a
// leaf is written from its id and an internal node from its name.
//
// `digits` caps branch-length precision; trailing zeros are trimmed so a length
// of 1 prints as "1", not "1.0000". The root carries no branch length, matching
// the canonical "(...);" form.
export function serialiseNewick(graph, { digits = 4 } = {}) {
  const root = graph?.root ?? graph?.preorderTraversal?.[0] ?? graph;

  const fmt = (n) => Number(n ?? 0).toFixed(digits).replace(/\.?0+$/, "");
  const label = (node) => (node.isLeaf ? node.id : node.name) ?? "";

  function walk(node, isRoot) {
    const subtree = node.children?.length
      ? `(${node.children.map((child) => walk(child, false)).join(",")})`
      : "";
    const length = isRoot ? "" : `:${fmt(node.branchLength)}`;
    return `${subtree}${label(node)}${length}`;
  }

  return `${walk(root, true)};`;
}
